import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PencilLine } from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import OnboardingTemplate from '../OnboardingTemplate';
import { useAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

interface ProfilePictureStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ProfilePictureStep: React.FC<ProfilePictureStepProps> = ({ onNext, onPrevious }) => {
  const { user } = useAuth();
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stepper/progress bar content
  const step = 3;
  const totalSteps = 3;
  const stepperContent = (
    <div className="mb-8">
      <div className="flex items-center mb-4">
        {[...Array(totalSteps)].map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 mx-4 rounded transition-colors duration-300 ${i === 2 ? 'bg-orange-500' : 'bg-gray-200'}`}
            style={{ maxWidth: 180 }}
          />
        ))}
      </div>
      <div className="text-xs text-muted-foreground mb-2">Step {step} of {totalSteps}</div>
    </div>
  );

  // Get user's first initial for fallback
  const userInitial = 'A';

  const handleAvatarClick = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleNext = async () => {
    if (!user?.id) return;
    await supabase
      .from('profiles')
      .update({
        avatar_url: avatarPreview,
        onboarding_status: 'in_progress',
        onboarding_step: 5
      })
      .eq('id', user.id);
    onNext();
  };

  return (
    <OnboardingTemplate showStepper={true} stepperContent={stepperContent}>
      <h1 className="text-3xl font-bold mb-6">Profile Picture</h1>
      <div className="flex flex-col items-center justify-center mb-8">
        <div className="relative group mb-4" style={{ width: 120, height: 120 }}>
          <Avatar className="w-[120px] h-[120px] text-5xl bg-gray-100">
            {avatarPreview && !isUploading ? (
              <AvatarImage src={avatarPreview} alt="Profile picture" />
            ) : null}
            <AvatarFallback>{userInitial}</AvatarFallback>
          </Avatar>
          <button
            type="button"
            className="absolute bottom-2 right-2 bg-white rounded-full p-1 shadow group-hover:shadow-lg border border-gray-200 hover:bg-gray-50 transition"
            onClick={handleAvatarClick}
            tabIndex={0}
            aria-label="Upload profile picture"
            disabled={isUploading}
            style={{ zIndex: 2 }}
          >
            <PencilLine className="w-5 h-5 text-gray-500" />
          </button>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleAvatarChange}
            disabled={isUploading}
          />
        </div>
        <div className="text-center text-gray-500 text-sm">
          Upload a clear, professional profile picture<br/>
          (JPEG, PNG, GIF, WEBP)<br/>
          <span className="text-xs">GIFs will preserve their animation</span>
        </div>
      </div>
      <div className="flex flex-row gap-2 pt-4">
        <Button variant="outline" onClick={onPrevious} className="w-1/2">
          Back
        </Button>
        <Button onClick={handleNext} className="w-1/2">
          Next
        </Button>
      </div>
    </OnboardingTemplate>
  );
};

export default ProfilePictureStep; 